import usePublicEventsService from "@app-contexts/public/features/event-detail/data/services/usePublicEventsService";
import { useDisclosure } from "@shared/hooks/useDisclosure";
import { enums, PAYMENT_TYPE_OPTIONS_MAP } from "@shared/constants";
import { useEmbeddedComponent } from "@shared/hooks/useEmbeddedComponent";
import { getEmbeddedPurchasePlatform } from "@shared/modules/financials/utils/purchase-platform-utils";
import { waitForElementToLoad } from "@shared/services/loaders/element-loader";
import cdn from "@shared/utils/urls/cdn";
import throttle from "lodash/throttle";
import { useEffect } from "react";
import { MapBannerProps } from "./MapBanner.props";

export function useMapBanner({
  venueObject,
  seatingObject,
  updateHideReadMoreButton,
}: MapBannerProps & {
  updateHideReadMoreButton: (input: boolean) => void;
}) {
  const { isEmbedded } = useEmbeddedComponent();

  // refund policy text variables
  const refundPolicyId = "refundPolicyText";
  const maxRefundTextHeight = 36; // the height of 2 lines of text

  // shows or hides the Read More button in the navbar, underneath the refund policy text
  const handleReadMoreButton = () => {
    const refundTextHeight =
      document?.getElementById(refundPolicyId)?.scrollHeight ?? 0;
    const shortRefundText = refundTextHeight <= maxRefundTextHeight;

    updateHideReadMoreButton(shortRefundText);
  };

  useEffect(() => {
    waitForElementToLoad(`#${refundPolicyId}`).then(() => {
      // display or hide read more button on page load
      handleReadMoreButton();

      let handleResizeDelay;
      // add delay to function call so that we get the read more button display at the end of the resize event
      window.addEventListener("resize", () => {
        clearTimeout(handleResizeDelay);
        handleResizeDelay = setTimeout(
          throttle(() => {
            handleReadMoreButton();
          }, 250),
          100
        );
      });
    });
    // this useEffect should only run once, so no dependencies
  }, []); // eslint-disable-line

  // event info modal variables (opens from the More Info button)
  const {
    isOpen: isInfoModalOpen,
    onOpen: onOpenInfoModal,
    onClose: onCloseInfoModal,
  } = useDisclosure();

  // help and info modal variables (opens from the refund policy section)
  const {
    isOpen: isHelpAndInfoModalOpen,
    onOpen: onOpenHelpAndInfoModal,
    onClose: onCloseHelpAndInfoModal,
  } = useDisclosure();
  // help and info modal variables (opens from the refund policy section)
  const {
    isOpen: isContactDetailsModalOpen,
    onOpen: onOpenContactDetailsModal,
    onClose: onCloseContactDetailsModal,
  } = useDisclosure();

  // timeslot modal variables (opens only on mobile, from clicking event name/date/location in the header)
  const {
    isOpen: isTimeslotModalOpen,
    onOpen: onOpenTimeslotModal,
    onClose: onCloseTimeslotModal,
  } = useDisclosure();

  const refundPolicyText =
    seatingObject?.refund_policy || venueObject?.default_refund_policy;

  // Get image source otherwise display default banner from static files
  const getImageSource = (): string => {
    // default image
    let imageSource = cdn("images/default-banner.png");

    if (seatingObject?.image_banner) {
      imageSource = seatingObject.image_banner;
    }
    return imageSource;
  };

  const eventParams = {
    // if on the seating widget, use widget logic for the purchase platform
    purchase_platform: isEmbedded
      ? getEmbeddedPurchasePlatform()
      : enums.PURCHASE_SOURCE_PLATFORM_MAP.PLATFORM_WEB.id,
    payment_type: [
      PAYMENT_TYPE_OPTIONS_MAP.CREDIT.id,
      PAYMENT_TYPE_OPTIONS_MAP.AFTERPAY.id,
    ].toString(),
  };

  // fetch parent event data for recurring assigned seating events
  const { useRetrieve } = usePublicEventsService();
  const { data: parentEventData } = useRetrieve(seatingObject?.parent_event, {
    config: {
      params: eventParams,
    },
    options: {
      keepPreviousData: true, // cache data
      enabled: Boolean(seatingObject && seatingObject.parent_event),
    },
  });

  return {
    refundPolicyId,
    refundPolicyText,
    parentEventData,
    isInfoModalOpen,
    onOpenInfoModal,
    onCloseInfoModal,
    isHelpAndInfoModalOpen,
    onOpenHelpAndInfoModal,
    onCloseHelpAndInfoModal,
    isTimeslotModalOpen,
    onOpenTimeslotModal,
    onCloseTimeslotModal,
    getImageSource,
    isContactDetailsModalOpen,
    onOpenContactDetailsModal,
    onCloseContactDetailsModal,
  };
}
